/**
 * Buscador de menús (Ctrl+K en Windows/Linux, ⌘K en Mac).
 *
 * Se monta una sola vez en `AdminLayout`, que envuelve TODAS las rutas `/admin/*`
 * de todos los árboles de App.tsx (admin y subdominios de portal) y ya va detrás
 * de `ProtectedRoute`: funciona en cualquier portal y solo con sesión.
 *
 * Fuente de datos: `useDynamicMenus`, el mismo hook del sidebar. Ahí ya se
 * resolvieron rol, permiso `leer`, menús bloqueados por individualización y
 * `solo_usuarioa`; aquí no se consulta ni se filtra nada por permisos. Las
 * vistas del rol Inmobiliaria con datos incompletos salen con candado, con la
 * misma regla que el sidebar (`lib/rutasBloqueadasInmobiliaria.ts`).
 *
 * La lógica pura (ranking, resaltado, recientes, atajo, destino) vive en
 * `lib/buscadorMenus.ts`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Clock, Home, LayoutDashboard, Loader2, Lock, Search, Star, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { IconTip } from "@/components/ui/icon-tip";
import { cn } from "@/lib/utils";
import { CONTROL_FILTRO_CLS } from "@/components/ui/filter-field";
import { useAuth } from "@/contexts/AuthContext";
import { useDynamicMenus } from "@/hooks/useDynamicMenus";
import { CURRENT_PORTAL_SUBDOMAIN, PORTAL_ROUTE_PREFIX, getPortalHost, type PortalKey } from "@/lib/portalUrls";
import {
  accionDeTeclaEnFila,
  alternarGrupoGuardado,
  grupoDeValor,
  leerGruposAbiertos,
  valorEncabezado,
  vistaGrupos,
  type GrupoVista,
  alternarFavoritoGuardado,
  borrarRecientesGuardados,
  construirOpciones,
  ID_IR_AL_ADMIN,
  leerFavoritos,
  opcionesFavoritas,
  parsearValorFila,
  podarGuardados,
  quitarRecienteGuardado,
  seleccionSiguiente,
  valorDeFila,
  type SeccionFila,
  alcanceBuscador,
  busquedaPendiente,
  consultaParaBuscar,
  DEBOUNCE_BUSQUEDA_MS,
  primeraAbrible,
  debeIgnorarAtajo,
  leerRecientes,
  opcionesRecientes,
  registrarReciente,
  resaltar,
  resolverDestino,
  type OpcionBuscador,
  type Rango,
} from "@/lib/buscadorMenus";
import { useInmobiliariaDataStatus } from "@/hooks/useInmobiliariaDataStatus";
import { useAtajoAgenteCeo } from "@/hooks/useAtajoAgenteCeo";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  combinacionDe,
  esMac,
  ejecutarDuenoAtajo,
  hayDuenoAtajo,
  resolverAtajo,
  type OpcionSelector,
} from "@/lib/atajosTeclado";
import { SelectorAtajoK } from "./SelectorAtajoK";
import { ROL_INMOBILIARIA, esRutaBloqueadaInmobiliaria } from "@/lib/rutasBloqueadasInmobiliaria";
import type { LucideIcon } from "lucide-react";

const EVENTO_ABRIR = "sozu:abrir-buscador-menus";

/** Abre el buscador desde cualquier botón (header de un portal, por ejemplo). */
export function abrirBuscadorMenus() {
  window.dispatchEvent(new Event(EVENTO_ABRIR));
}

/** Almacén local del navegador, o null si el navegador lo bloquea. */
function almacenLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Montaje + atajo
// ---------------------------------------------------------------------------

export function BuscadorMenus() {
  const [abierto, setAbierto] = useState(false);
  // Los menús se piden la primera vez que se abre (no en cada pantalla) y el
  // diálogo se queda montado: reabrir es instantáneo.
  const [usado, setUsado] = useState(false);
  const [selector, setSelector] = useState<OpcionSelector[] | null>(null);

  const { profile } = useAuth();
  const atajoCeo = useAtajoAgenteCeo();
  // El listener vive una sola vez; lee lo vigente por referencia.
  const ctxRef = useRef({ atajoCeo, esSuperAdmin: false, selectorAbierto: false });
  ctxRef.current = { atajoCeo, esSuperAdmin: profile?.rol_id === 1, selectorAbierto: selector !== null };

  useEffect(() => {
    const mac = esMac();
    const onKey = (e: KeyboardEvent) => {
      // Captura en window: corre antes que cualquier otro listener y antes de
      // que Chrome mande el foco a su barra de búsqueda.
      const objetivo = e.target as Element | null;
      const combinacion = combinacionDe(e, mac, objetivo);
      if (!combinacion || debeIgnorarAtajo(objetivo)) return;
      const { atajoCeo: estado, esSuperAdmin, selectorAbierto } = ctxRef.current;
      const accion = resolverAtajo(combinacion, {
        atajoCeo: estado,
        agenteCeoMontado: hayDuenoAtajo("agenteCeo"),
        panoramaMontado: hayDuenoAtajo("panorama"),
        esSuperAdmin,
      });
      if (accion.tipo === "ninguna") return;
      // Cancelar aquí también cancela el keypress/beforeinput (el "˚" de ⌥K en Mac).
      e.preventDefault();
      e.stopImmediatePropagation();
      if (accion.tipo === "dueno") {
        ejecutarDuenoAtajo(accion.dueno);
      } else if (accion.tipo === "selector") {
        // Repetir la combinación con el selector abierto lo cierra.
        setSelector(selectorAbierto ? null : accion.opciones);
      } else {
        setSelector(null);
        setUsado(true);
        // "/" solo abre (es una tecla que también se escribe); Ctrl/⌘+K abre y cierra.
        setAbierto((v) => (combinacion === "slash" ? true : !v));
      }
    };
    const onAbrir = () => {
      setUsado(true);
      setAbierto(true);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    window.addEventListener(EVENTO_ABRIR, onAbrir);
    return () => {
      window.removeEventListener("keydown", onKey, { capture: true });
      window.removeEventListener(EVENTO_ABRIR, onAbrir);
    };
  }, []);

  const elegirDelSelector = useCallback((opcion: OpcionSelector) => {
    setSelector(null);
    if (opcion === "agenteCeo") ejecutarDuenoAtajo("agenteCeo");
    else if (opcion === "buscador") {
      setUsado(true);
      setAbierto(true);
    }
    // "cambiarAtajoCeo" lo resuelve el propio selector con su confirmación.
  }, []);

  return (
    <>
      <SelectorAtajoK
        opciones={selector}
        onCerrar={() => setSelector(null)}
        onElegir={elegirDelSelector}
      />
      {usado && <BuscadorMenusDialogo abierto={abierto} onAbiertoChange={setAbierto} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Botón del header
// ---------------------------------------------------------------------------

export function BotonBuscadorMenus({ className }: { className?: string }) {
  const [mac] = useState(esMac);
  // Tooltip con el atajo alterno "/": sirve cuando el navegador se queda con
  // Ctrl/⌘+K (por ejemplo, con el foco en la barra de direcciones).
  const atajo = mac ? "⌘K" : "Ctrl+K";
  return (
    <IconTip label={`Buscar menú con ${atajo}, o con / fuera de un campo de texto`}>
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={abrirBuscadorMenus}
      aria-label="Buscar menú"
      aria-keyshortcuts={mac ? "Meta+K /" : "Control+K /"}
      className={cn(
        // Mismo control que el selector "Ver como" a su lado: 36 px, borde y fondo de input estándar.
        "h-9 gap-2 px-3 text-[13px] font-normal text-muted-foreground hover:bg-background hover:text-foreground max-sm:w-9 max-sm:px-0 sm:w-[260px] sm:justify-start",
        // En móvil solo el ícono, sin caja, como el tema y la campana de junto.
        CONTROL_FILTRO_CLS,
        "max-sm:border-transparent max-sm:bg-transparent max-sm:shadow-none max-sm:hover:bg-accent",
        className,
      )}
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="hidden sm:inline">Buscar menú</span>
      <kbd className="pointer-events-none ml-auto hidden h-5 select-none items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
        {mac ? "⌘" : "Ctrl"} K
      </kbd>
    </Button>
    </IconTip>
  );
}

// ---------------------------------------------------------------------------
// Diálogo
// ---------------------------------------------------------------------------

type Opcion = OpcionBuscador<LucideIcon>;

function Resaltado({ texto, rangos }: { texto: string; rangos: Rango[] }) {
  return (
    <>
      {resaltar(texto, rangos).map((s, i) =>
        s.resaltado ? (
          <mark key={i} className="rounded-sm bg-primary/15 font-semibold text-foreground">
            {s.texto}
          </mark>
        ) : (
          <span key={i}>{s.texto}</span>
        ),
      )}
    </>
  );
}

/** Botón chico de acción dentro de una fila: no debe abrir la fila al hacer clic. */
function AccionFila({
  etiqueta,
  onClick,
  presionado,
  children,
}: {
  etiqueta: string;
  onClick: () => void;
  presionado?: boolean;
  children: ReactNode;
}) {
  return (
    <IconTip label={etiqueta}>
      <button
        type="button"
        tabIndex={-1}
        aria-label={etiqueta}
        aria-pressed={presionado}
        onPointerDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {children}
      </button>
    </IconTip>
  );
}

function FilaOpcion({
  opcion,
  rangos,
  seccion,
  onElegir,
  bloqueada,
  favorito,
  onAlternarFavorito,
  onQuitarReciente,
  suelta,
}: {
  /** Menú directo sin encabezado (Dashboard): fila de primer nivel, sin sangría. */
  suelta?: boolean;
  opcion: Opcion;
  rangos: Rango[];
  seccion: SeccionFila;
  onElegir: (o: Opcion) => void;
  /** Igual que el candado del sidebar: se ve, pero no se abre. */
  bloqueada?: boolean;
  favorito: boolean;
  onAlternarFavorito: (o: Opcion) => void;
  onQuitarReciente?: (o: Opcion) => void;
}) {
  // Bajo el encabezado de su menú la fila es un hijo: sangría, guía vertical y
  // sin repetir el nombre del grupo. Sueltas (Recientes, Favoritos) llevan la
  // ruta completa y, si es la entrada a un portal, el chip "Portal".
  const enGrupo = seccion === "resultados" && !suelta;
  const Icono =
    seccion === "recientes" ? Clock : seccion === "favoritos" ? Star : opcion.esEntradaPortal ? Home : opcion.icono;
  const subtitulo = bloqueada
    ? "Sección bloqueada. Completa tu información en Mi información para habilitarla."
    : enGrupo || suelta
      ? null
      : opcion.ruta;
  return (
    <CommandItem
      value={valorDeFila(seccion, opcion.id)}
      disabled={bloqueada}
      onSelect={() => onElegir(opcion)}
      className={cn("group gap-3", enGrupo ? "ml-4 rounded-l-none border-l border-border py-1.5 pl-3" : "py-2")}
    >
      <Icono className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn("truncate text-sm", opcion.esEntradaPortal && enGrupo && "font-medium")}>
            <Resaltado texto={opcion.titulo} rangos={rangos} />
          </span>
          {!enGrupo && opcion.esEntradaPortal && <ChipTipo texto="Portal" />}
        </span>
        {subtitulo && <span className="truncate text-xs text-muted-foreground">{subtitulo}</span>}
      </div>
      {bloqueada && <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Bloqueada" />}
      {/* pointer-events-auto: cmdk apaga los eventos de las filas deshabilitadas. */}
      <div className="pointer-events-auto flex shrink-0 items-center gap-0.5">
        <AccionFila
          etiqueta={favorito ? "Quitar de favoritos" : "Agregar a favoritos"}
          presionado={favorito}
          onClick={() => onAlternarFavorito(opcion)}
        >
          <Star className={cn("h-3.5 w-3.5", favorito && "fill-amber-400 text-amber-500")} aria-hidden="true" />
        </AccionFila>
        {onQuitarReciente && (
          <AccionFila etiqueta="Quitar de recientes" onClick={() => onQuitarReciente(opcion)}>
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </AccionFila>
        )}
      </div>
    </CommandItem>
  );
}

/** Chip discreto de tipo: solo donde ayuda a distinguir (portales). */
function ChipTipo({ texto }: { texto: string }) {
  return (
    <span className="shrink-0 rounded border border-border px-1.5 py-px text-[10px] font-medium leading-4 text-muted-foreground">
      {texto}
    </span>
  );
}

/** Rótulo de las secciones Favoritos y Recientes (los menús usan EncabezadoContraible). */
function EncabezadoGrupo({ icono: Icono, texto }: { icono: LucideIcon; texto: string; suave?: boolean }) {
  return (
    <span className="flex items-center gap-1.5">
      <Icono className="h-3.5 w-3.5" aria-hidden="true" />
      {texto}
    </span>
  );
}

/**
 * Encabezado contraíble de un portal o menú: es una opción de cmdk (las flechas
 * pasan por él) que abre y cierra el grupo. Enter, clic o → lo abren; ← lo cierra.
 * Nunca navega.
 */
function EncabezadoContraible({
  grupo,
  onAlternar,
}: {
  grupo: GrupoVista<LucideIcon>;
  onAlternar: (g: GrupoVista<LucideIcon>) => void;
}) {
  const Chevron = grupo.expandido ? ChevronDown : ChevronRight;
  const Icono = grupo.icono;
  return (
    <CommandItem
      value={valorEncabezado(grupo.grupoId)}
      onSelect={() => onAlternar(grupo)}
      aria-expanded={grupo.expandido}
      className="mt-1 gap-2 rounded-md bg-muted/60 px-2 py-1.5 text-[13px] font-semibold text-foreground"
    >
      <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <Icono className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{grupo.grupo || "Sin nombre"}</span>
      {grupo.esPortal && <ChipTipo texto="Portal" />}
      <span
        className="shrink-0 rounded-full bg-background px-2 py-px text-[10px] font-medium leading-4 text-muted-foreground"
        aria-label={grupo.parcial ? `${grupo.resultados.length} de ${grupo.total} opciones` : `${grupo.total} opciones`}
      >
        {grupo.parcial ? `${grupo.resultados.length} de ${grupo.total}` : grupo.total}
      </span>
    </CommandItem>
  );
}

function BuscadorMenusDialogo({
  abierto,
  onAbiertoChange,
}: {
  abierto: boolean;
  onAbiertoChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { menuItems, isLoading, isSuperAdmin } = useDynamicMenus();
  const usuario = profile?.email ?? user?.email ?? null;

  // Mismo bloqueo que el sidebar para el rol Inmobiliaria con datos incompletos.
  const isInmobiliariaRole = profile?.rol_id === ROL_INMOBILIARIA;
  const { isDataComplete } = useInmobiliariaDataStatus(isInmobiliariaRole ? profile?.id_persona : null);
  const esBloqueada = useCallback(
    (o: Opcion) => esRutaBloqueadaInmobiliaria(o.href, { isSuperAdmin, isInmobiliariaRole, isDataComplete }),
    [isSuperAdmin, isInmobiliariaRole, isDataComplete],
  );

  // Dentro de un portal solo se busca en ese portal (más "Ir al admin principal");
  // en el admin principal, en todo lo del usuario.
  const { pathname } = useLocation();
  const { portal: portalDeBusqueda, opciones } = useMemo(
    () =>
      alcanceBuscador<LucideIcon>(menuItems, {
        pathname,
        prefijoSubdominio: CURRENT_PORTAL_SUBDOMAIN ? PORTAL_ROUTE_PREFIX[CURRENT_PORTAL_SUBDOMAIN] : null,
        esSuperAdmin: isSuperAdmin,
        iconoAdmin: LayoutDashboard,
      }),
    [menuItems, pathname, isSuperAdmin],
  );

  const [consulta, setConsulta] = useState("");
  const [seleccion, setSeleccion] = useState("");
  const [recientesIds, setRecientesIds] = useState<string[]>([]);
  const [favoritosIds, setFavoritosIds] = useState<string[]>([]);

  // Al abrir: consulta en blanco, recientes y favoritos al día (pudieron cambiar
  // en otra pestaña).
  useEffect(() => {
    if (!abierto) return;
    setConsulta("");
    setRecientesIds(leerRecientes(almacenLocal(), usuario));
    setFavoritosIds(leerFavoritos(almacenLocal(), usuario));
  }, [abierto, usuario]);

  // Al validar los menús: fuera lo que ya no puede abrir. Contra TODAS sus
  // opciones, no solo las del portal actual, para no perder favoritos de otro portal.
  useEffect(() => {
    if (!abierto || isLoading || menuItems.length === 0) return;
    const validos = new Set(construirOpciones(menuItems).map((o) => o.id));
    validos.add(ID_IR_AL_ADMIN);
    const podados = podarGuardados(almacenLocal(), usuario, validos);
    setFavoritosIds(podados.favoritos);
    setRecientesIds(podados.recientes);
  }, [abierto, isLoading, menuItems, usuario]);

  const favoritosSet = useMemo(() => new Set(favoritosIds), [favoritosIds]);
  const alternarFavorito = useCallback(
    (o: Opcion) => setFavoritosIds(alternarFavoritoGuardado(almacenLocal(), usuario, o.id)),
    [usuario],
  );
  const quitarReciente = useCallback(
    (o: Opcion) => setRecientesIds(quitarRecienteGuardado(almacenLocal(), usuario, o.id)),
    [usuario],
  );
  const borrarRecientes = () => setRecientesIds(borrarRecientesGuardados(almacenLocal(), usuario));

  // Grupos abiertos sin consulta (se guardan por usuario) y grupos expandidos a
  // mano durante una consulta (se olvidan al cambiarla).
  const [abiertos, setAbiertos] = useState<number[]>([]);
  const [forzados, setForzados] = useState<number[]>([]);
  useEffect(() => {
    if (abierto) setAbiertos(leerGruposAbiertos(almacenLocal(), usuario));
  }, [abierto, usuario]);

  // El input muestra lo escrito al instante; los resultados se recalculan
  // DEBOUNCE_BUSQUEDA_MS después de la última tecla (vacío, al instante).
  const consultaDebounce = useDebouncedValue(consulta, DEBOUNCE_BUSQUEDA_MS);
  const consultaBuscada = consultaParaBuscar(consulta, consultaDebounce);
  const hayConsulta = consultaBuscada.trim().length > 0;
  // Un fallo al buscar no debe tumbar la app: ErrorBoundaryGlobal recarga la
  // página ante cualquier error de render. Se muestra un aviso en su lugar.
  useEffect(() => setForzados([]), [consultaBuscada]);
  const { vistas, fallo } = useMemo(() => {
    try {
      // Sin consulta: grupos contraídos salvo los abiertos. Con consulta: el grupo
      // de nombre más exacto completo y, en los demás, solo lo que coincide. Los
      // favoritos que coinciden suben un poco.
      const v = vistaGrupos(opciones, consultaBuscada, {
        abiertos: new Set(abiertos),
        forzados: new Set(forzados),
        favoritos: favoritosSet,
      });
      return { vistas: v, fallo: false };
    } catch (err) {
      console.error("[BuscadorMenus] no se pudo buscar:", err);
      return { vistas: [] as GrupoVista<LucideIcon>[], fallo: true };
    }
  }, [opciones, consultaBuscada, favoritosSet, abiertos, forzados]);

  const alternarGrupo = useCallback(
    (g: GrupoVista<LucideIcon>, abrir?: boolean) => {
      const quiere = abrir ?? !g.expandido;
      if (consultaBuscada.trim()) {
        // Con consulta: abrir muestra el grupo completo; cerrar lo deja como estaba.
        setForzados((f) => (quiere ? [...new Set([...f, g.grupoId])] : f.filter((x) => x !== g.grupoId)));
      } else {
        setAbiertos(alternarGrupoGuardado(almacenLocal(), usuario, g.grupoId, quiere));
      }
    },
    [consultaBuscada, usuario],
  );
  // Sin consulta: Favoritos (del alcance actual, máximo 10) y Recientes arriba.
  const favoritos = useMemo(
    () => (hayConsulta ? [] : opcionesFavoritas(favoritosIds, opciones)),
    [hayConsulta, favoritosIds, opciones],
  );
  const recientes = useMemo(
    () => (hayConsulta ? [] : opcionesRecientes(recientesIds, opciones)),
    [hayConsulta, recientesIds, opciones],
  );

  // Siempre hay una fila resaltada: la primera (la de mayor puntaje) al abrir y
  // cada vez que cambian los resultados de una consulta nueva. Lo elegido con
  // flechas se respeta mientras la consulta no cambie. Resaltar nunca navega.
  const visibles = useMemo(
    () => [
      ...favoritos.filter((o) => !esBloqueada(o)).map((o) => valorDeFila("favoritos", o.id)),
      ...recientes.filter((o) => !esBloqueada(o)).map((o) => valorDeFila("recientes", o.id)),
      ...vistas.flatMap((g) => [
        ...(g.colapsable ? [valorEncabezado(g.grupoId)] : []),
        ...g.resultados.filter((r) => !esBloqueada(r.opcion)).map((r) => r.opcion.id),
      ]),
    ],
    [favoritos, recientes, vistas, esBloqueada],
  );
  // Se preselecciona una fila (no un encabezado) si la hay: la del mejor resultado.
  const preferido = visibles.find((v) => grupoDeValor(v) === null);
  const claveVisibles = visibles.join("|");
  const claveConsulta = abierto ? consultaBuscada : null;
  const ultimaConsulta = useRef<string | null>(null);
  useEffect(() => {
    const cambio = ultimaConsulta.current !== claveConsulta;
    ultimaConsulta.current = claveConsulta;
    setSeleccion((actual) => seleccionSiguiente(actual, visibles, cambio, preferido));
    // `visibles` cambia junto con `claveVisibles`; la clave evita re-ejecutar por identidad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveVisibles, claveConsulta]);

  const elegir = useCallback(
    (opcion: Opcion) => {
      if (esBloqueada(opcion)) return;
      const destino = resolverDestino(opcion.href, {
        subdominioActual: CURRENT_PORTAL_SUBDOMAIN,
        prefijos: PORTAL_ROUTE_PREFIX,
        host: (clave) => getPortalHost(clave as PortalKey),
      });
      if (!destino) return;
      setRecientesIds(registrarReciente(almacenLocal(), usuario, opcion.id));
      onAbiertoChange(false);
      // Siempre en la misma pestaña, como el sidebar.
      if (destino.tipo === "interno") navigate(destino.ruta);
      else window.location.assign(destino.url);
    },
    [esBloqueada, navigate, onAbiertoChange, usuario],
  );

  // Esc lo cierra el Dialog; ↑/↓ y Enter los maneja cmdk. Aquí solo se evita
  // que Ctrl/⌘+Enter haga algo distinto de Enter.
  const porId = useMemo(() => new Map(opciones.map((o) => [o.id, o])), [opciones]);
  const [mac] = useState(esMac);

  const vistaPorGrupo = useMemo(() => new Map(vistas.map((g) => [g.grupoId, g])), [vistas]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    // →/← sobre un encabezado lo abren o cierran; ← sobre una fila de un grupo
    // abierto (sin texto en el input, para no robar el cursor) lo cierra y vuelve
    // al encabezado.
    if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
      const idEncabezado = grupoDeValor(seleccion);
      if (idEncabezado !== null) {
        const g = vistaPorGrupo.get(idEncabezado);
        if (g) {
          e.preventDefault();
          alternarGrupo(g, e.key === "ArrowRight");
        }
        return;
      }
      if (e.key === "ArrowLeft" && consulta.length === 0) {
        const fila = parsearValorFila(seleccion);
        const g = fila?.seccion === "resultados" ? vistas.find((v) => v.colapsable && v.resultados.some((r) => r.opcion.id === fila.id)) : undefined;
        if (g) {
          e.preventDefault();
          alternarGrupo(g, false);
          setSeleccion(valorEncabezado(g.grupoId));
        }
      }
      return;
    }
    // Atajos sobre la fila activa: Ctrl/⌘+D favorito, Supr quita de Recientes.
    const fila = grupoDeValor(seleccion) === null ? parsearValorFila(seleccion) : null;
    const accion = accionDeTeclaEnFila(e, mac, fila, consulta.length > 0);
    if (accion && fila) {
      e.preventDefault();
      const opcion = porId.get(fila.id);
      if (opcion && accion === "alternarFavorito") alternarFavorito(opcion);
      if (opcion && accion === "quitarReciente") quitarReciente(opcion);
      return;
    }
    if (e.key !== "Enter") return;
    // Ctrl/⌘+Enter no hace nada distinto de Enter: todo abre en la misma pestaña.
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      return;
    }
    // Enter durante el debounce: se busca con lo escrito ahora, no con lo viejo
    // que sigue en pantalla. preventDefault evita que cmdk abra la fila vieja.
    if (busquedaPendiente(consulta, consultaDebounce)) {
      e.preventDefault();
      try {
        const filas = vistaGrupos(opciones, consulta, { favoritos: favoritosSet }).flatMap((g) => g.resultados);
        const opcion = primeraAbrible(filas, esBloqueada);
        if (opcion) elegir(opcion);
      } catch (err) {
        console.error("[BuscadorMenus] no se pudo buscar:", err);
      }
    }
  };

  const totalVisibles = favoritos.length + recientes.length + vistas.length;
  const propsFila = { onElegir: elegir, onAlternarFavorito: alternarFavorito };

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      {/* data-buscador-menus (ATRIBUTO_BUSCADOR): dentro, Ctrl/⌘+K cierra en vez de ignorarse. */}
      <DialogContent data-buscador-menus="" className="max-w-xl gap-0 overflow-hidden p-0 sm:top-[18%] sm:translate-y-0">
        <DialogTitle className="border-b px-4 py-3 pr-12 text-xs font-medium text-muted-foreground">
          {portalDeBusqueda ? `Buscando en ${portalDeBusqueda}` : "Buscando en todos tus menús"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Escribe para filtrar los menús a los que tienes acceso. Usa las flechas para moverte y Enter para abrir.
        </DialogDescription>
        <Command
          shouldFilter={false}
          loop
          value={seleccion}
          onValueChange={setSeleccion}
          onKeyDown={onKeyDown}
          label="Buscar menú"
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-input]]:h-12"
        >
          <CommandInput
            value={consulta}
            onValueChange={setConsulta}
            placeholder="Buscar menú o portal"
            aria-label="Buscar menú o portal"
            className="pr-8"
          />
          <CommandList className="max-h-[min(60vh,440px)]" aria-label="Menús disponibles">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" role="status">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Cargando tus menús
              </div>
            ) : opciones.length === 0 ? (
              <p className="px-6 py-10 text-center text-sm text-muted-foreground" role="status">
                Tu usuario todavía no tiene menús asignados. Si crees que es un error, pide acceso a un administrador.
              </p>
            ) : fallo ? (
              <p className="px-6 py-10 text-center text-sm text-muted-foreground" role="alert">
                No pudimos buscar, intenta de nuevo.
              </p>
            ) : totalVisibles === 0 ? (
              <p className="px-6 py-10 text-center text-sm text-muted-foreground" role="status">
                {portalDeBusqueda
                  ? `No encontramos un menú con “${consultaBuscada.trim()}” en ${portalDeBusqueda}.`
                  : `No encontramos un menú con “${consultaBuscada.trim()}” entre los que tienes acceso.`}
              </p>
            ) : (
              <>
                {favoritos.length > 0 && (
                  <CommandGroup heading={<EncabezadoGrupo icono={Star} texto="Favoritos" suave />}>
                    {favoritos.map((o) => (
                      <FilaOpcion
                        key={`f-${o.id}`}
                        opcion={o}
                        rangos={[]}
                        seccion="favoritos"
                        bloqueada={esBloqueada(o)}
                        favorito
                        {...propsFila}
                      />
                    ))}
                  </CommandGroup>
                )}
                {recientes.length > 0 && (
                  <>
                    {/* El encabezado de cmdk es aria-hidden: el botón va fuera de él. */}
                    <div className="flex items-center justify-between px-4 pt-2 text-xs font-medium text-muted-foreground">
                      <span aria-hidden="true">
                        <EncabezadoGrupo icono={Clock} texto="Recientes" suave />
                      </span>
                      <button
                        type="button"
                        onClick={borrarRecientes}
                        className="rounded px-1.5 py-0.5 text-[11px] hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        Borrar recientes
                      </button>
                    </div>
                    <CommandGroup heading={<span className="sr-only">Recientes</span>} className="[&_[cmdk-group-heading]]:p-0">
                      {recientes.map((o) => (
                        <FilaOpcion
                          key={`r-${o.id}`}
                          opcion={o}
                          rangos={[]}
                          seccion="recientes"
                          bloqueada={esBloqueada(o)}
                          favorito={favoritosSet.has(o.id)}
                          onQuitarReciente={quitarReciente}
                          {...propsFila}
                        />
                      ))}
                    </CommandGroup>
                  </>
                )}
                {vistas.map((g) =>
                  g.colapsable ? (
                    <CommandGroup key={g.grupoId} aria-label={g.grupo || "Sin nombre"}>
                      <EncabezadoContraible grupo={g} onAlternar={alternarGrupo} />
                      {g.resultados.map((r) => (
                        <FilaOpcion
                          key={r.opcion.id}
                          opcion={r.opcion}
                          rangos={r.rangos}
                          seccion="resultados"
                          bloqueada={esBloqueada(r.opcion)}
                          favorito={favoritosSet.has(r.opcion.id)}
                          {...propsFila}
                        />
                      ))}
                    </CommandGroup>
                  ) : (
                    <CommandGroup key={g.grupoId} aria-label={g.grupo || "Sin nombre"}>
                      {g.resultados.map((r) => (
                        <FilaOpcion
                          key={r.opcion.id}
                          opcion={r.opcion}
                          rangos={r.rangos}
                          seccion="resultados"
                          suelta
                          bloqueada={esBloqueada(r.opcion)}
                          favorito={favoritosSet.has(r.opcion.id)}
                          {...propsFila}
                        />
                      ))}
                    </CommandGroup>
                  ),
                )}
              </>
            )}
          </CommandList>
          <div className="hidden flex-wrap items-center gap-x-4 gap-y-1 border-t px-3 py-2 text-[11px] text-muted-foreground sm:flex" aria-hidden="true">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              para moverte
            </span>
            <span className="flex items-center gap-1">
              <Kbd>Enter</Kbd>
              para abrir
            </span>
            <span className="flex items-center gap-1">
              <Kbd>Esc</Kbd>
              para cerrar
            </span>
            <span className="flex items-center gap-1">
              <Kbd>{mac ? "⌘" : "Ctrl"} D</Kbd>
              favorito
            </span>
            <span className="flex items-center gap-1">
              <Kbd>Supr</Kbd>
              quita de recientes
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border bg-muted px-1 font-mono text-[10px] font-medium">
      {children}
    </kbd>
  );
}
